import { ComponentChildren } from 'preact';
import { IcoLogo } from '@components/icons';
import { useCurrentTenant } from '@components/router/session-context';
import { Tenant } from 'server/types';
import { RpxResponse, rpx } from 'client/lib/rpx-client';
import { accentGradient, createBoxShadow } from 'shared/tenant-style-utils';
import { useImageUrl } from 'client/utils/cdn';

type Brand = RpxResponse<typeof rpx.branding.getBrand>;

export function LoginWrapper({ children, brand }: { brand?: Brand; children?: ComponentChildren }) {
  const tenant = useCurrentTenant();

  return (
    <TenantLoginWrapper brand={brand} tenant={tenant}>
      {children}
    </TenantLoginWrapper>
  );
}

function TenantLoginWrapper(props: {
  tenant: Tenant;
  brand?: Brand;
  children?: ComponentChildren;
}) {
  const brand = props.brand || props.tenant;
  const { name, showName } = brand;
  const loginBgUrl = useImageUrl(brand.loginBgUrl);
  const loginLayout = brand.loginLayout || 'centered';
  const borderRadius = brand.containerBorderRadius;
  const showGradient = brand.enableGradient !== false;
  const containerPadding = brand.containerPadding;
  const boxShadow = createBoxShadow({ ...brand, shadowValue: brand.containerShadow });
  const containerStyle = {
    borderRadius,
    boxShadow: boxShadow,
  };

  if (loginLayout === 'split') {
    return (
      <div class="w-full min-h-screen flex items-stretch">
        {/* Left side (form) */}
        <div class="w-full lg:w-1/2 flex items-center justify-center p-8 bg-white">
          <div
            class={`w-full max-w-md bg-white flex flex-col gap-6 ${
              brand.containerShadow === 0 ? '' : 'shadow-md'
            }`}
            style={{
              borderRadius,
              boxShadow,
              padding: containerPadding,
            }}
          >
            <div class="flex items-center justify-center">
              <div class="inline-flex items-center justify-center px-8 relative">
                <span class="inline-block h-16 sm:h-24">
                  <IcoLogo class="h-full max-w-full" url={brand.iconUrl} />
                </span>
              </div>
            </div>

            {showName && (
              <span
                class="text-indigo-700 text-2xl px-12 flex justify-center"
                style={{ color: brand.logoColor }}
              >
                {name}
              </span>
            )}

            {props.children}
          </div>
        </div>

        {/* Right side (image/background) */}
        <div
          class="hidden lg:block w-1/2 relative bg-cover bg-center"
          style={{
            backgroundColor: brand.backgroundColor,
            backgroundImage: loginBgUrl ? `url(${loginBgUrl})` : undefined,
          }}
        />
      </div>
    );
  }

  return (
    <div
      class="w-full min-h-screen p-10 py-40 rounded-xs flex items-center justify-center relative bg-gray-50 bg-cover"
      style={{
        backgroundImage: loginBgUrl ? `url(${loginBgUrl})` : undefined,
        backgroundColor: brand.backgroundColor,
      }}
    >
      <div class="relative py-3 my-4 w-lg max-w-full">
        {showGradient && (
          <div
            class={`absolute inset-0 bg-linear-to-r from-indigo-400 to-blue-400 shadow-lg transform -skew-y-6 rounded-sm mx-2.5 ${showGradient ? 'block' : 'none'}`}
            style={{
              backgroundImage: accentGradient(brand),
            }}
          ></div>
        )}
        <div
          class={`relative bg-white flex flex-col gap-6 ${brand.containerShadow === 0 ? '' : 'shadow-lg'}`}
          style={{
            ...containerStyle,
            padding: containerPadding,
            borderRadius: brand.containerBorderRadius || '4px',
            boxShadow,
          }}
        >
          <div
            class="theme-logo-color text-indigo-700 flex flex-col justify-center items-center mb-8"
            style={{ color: brand.logoColor }}
          >
            <IcoLogo class="h-16 sm:h-24 max-w-full" url={brand.iconUrl} />
            {showName && (
              <h1 class={`mt-4 text-center ${name?.length < 20 ? 'text-3xl' : 'text-xl'}`}>
                {name}
              </h1>
            )}
          </div>
          {props.children}
        </div>
      </div>
    </div>
  );
}
